import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Radio,
  RadioGroup,
  Switch,
  Typography,
} from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';

// ----------------------------
// Types
// ----------------------------
export interface FilterNode {
  label: string;
  checked?: boolean; // provided ONLY for leaves
  onChecked?: (checked: boolean) => void;
  children?: FilterNode[];
}

export interface ToggleFilter {
  key: string;
  label: string;
  checked: boolean; // controlled
  onChange: (checked: boolean) => void;
}

export interface BottomRadioOption {
  key: string;
  label: string;
}

export interface BottomRadioFilterGroup {
  key: string;
  title: string;
  selectedKey: string; // controlled
  onChange: (key: string) => void;
  options: BottomRadioOption[];
}

interface FiltersWithScopeProps {
  filters: FilterNode[];
  topToggleFilters?: ToggleFilter[];
  bottomRadioFilters?: BottomRadioFilterGroup[];
  indent?: number;
  treeTitle?: string;
  onResetFilters?: () => void;
}

// ----------------------------
// Recursive, controlled FilterTree
// ----------------------------
const FilterTree: React.FC<{
  nodes: FilterNode[];
  indent?: number;
}> = ({ nodes, indent = 2 }) => {
  const collectSubtree = (node: FilterNode, path: string) => {
    const out = [{ path, node }];
    if (node.children) {
      node.children.forEach((child, idx) => {
        out.push(...collectSubtree(child, `${path}.${idx}`));
      });
    }
    return out;
  };

  // Clicking a parent toggles all descendants by invoking their onChecked
  const setNodeChecked = (
    path: string,
    node: FilterNode,
    checked: boolean,
  ) => {
    const items = collectSubtree(node, path);
    items.forEach(({ node: n }) => n.onChecked?.(checked));
  };

  // Derive checked/indeterminate for parents from controlled leaf states
  const getNodeStatus = (
    node: FilterNode,
    path: string,
  ): { checked: boolean; indeterminate: boolean } => {
    if (!node.children || node.children.length === 0) {
      return { checked: !!node.checked, indeterminate: false };
    }
    const childStatuses = node.children.map((child, idx) =>
      getNodeStatus(child, `${path}.${idx}`),
    );
    const allChecked = childStatuses.every((s) => s.checked);
    const anyCheckedOrIndeterminate = childStatuses.some(
      (s) => s.checked || s.indeterminate,
    );
    return {
      checked: allChecked,
      indeterminate: anyCheckedOrIndeterminate && !allChecked,
    };
  };

  const renderNodes = (
    nodes: FilterNode[],
    parentPath = '',
  ): JSX.Element[] =>
    nodes.map((node, index) => {
      const path = parentPath ? `${parentPath}.${index}` : `${index}`;
      const { checked, indeterminate } = getNodeStatus(node, path);
      const children = node.children
        ? renderNodes(node.children, path)
        : [];

      return (
        <Box key={path} mt={-1} ml={parentPath ? indent : 0}>
          <FormControlLabel
            control={
              <Checkbox
                checked={checked}
                indeterminate={indeterminate}
                onChange={(e) =>
                  setNodeChecked(path, node, e.target.checked)
                }
              />
            }
            label={node.label}
          />
          {children.length > 0 && (
            <Box ml={indent}>
              <FormGroup>{children}</FormGroup>
            </Box>
          )}
        </Box>
      );
    });

  return <FormGroup>{renderNodes(nodes)}</FormGroup>;
};

// ----------------------------
// Main component with restored layout/styling
// ----------------------------
const FiltersWithScope: React.FC<FiltersWithScopeProps> = ({
  filters,
  topToggleFilters = [],
  bottomRadioFilters = [],
  indent = 2,
  treeTitle = 'Tilat',
  onResetFilters,
}) => {
  return (
    <Box
      sx={{
        border: 1,
        borderColor: '#dad0d0',
        p: 4,
        pt: 2,
        borderRadius: 2,
        position: 'relative',
      }}
    >
      {/* legend title */}
      <Box
        sx={{
          position: 'absolute',
          top: -10,
          left: 16,
          backgroundColor: 'white',
          px: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        <FilterAltIcon
          sx={{ fontSize: 20, color: 'text.secondary' }}
        />
        <Typography variant="body1" color="text.secondary">
          Suodattimet
        </Typography>
      </Box>

      <Box display={'flex'} justifyContent={'space-between'}>
        {/* top toggles (column) */}
        {topToggleFilters.length > 0 && (
          <FormGroup sx={{ flexDirection: 'column' }}>
            {topToggleFilters.map((tf) => (
              <FormControlLabel
                key={tf.key}
                control={
                  <Switch
                    checked={tf.checked}
                    onChange={(e) => tf.onChange(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Typography fontWeight="bold">
                    {tf.label}
                  </Typography>
                }
                sx={{ width: 'fit-content' }} // prevents full-width click area
              />
            ))}
          </FormGroup>
        )}
        {onResetFilters && (
          <Button onClick={onResetFilters}>Palauta oletukset</Button>
        )}
      </Box>

      {/* bottom content laid out in a ROW: tree on left, radios on right */}
      <Box display="flex" gap={6}>
        {/* Left: Tree */}
        <Box>
          <Typography
            variant="subtitle1"
            gutterBottom
            sx={{
              fontWeight: 'bold',
              mt: topToggleFilters.length ? 2 : 0,
            }}
          >
            {treeTitle}
          </Typography>

          <FilterTree nodes={filters} indent={indent} />
        </Box>

        {/* Right: Radio groups */}
        <Box>
          {bottomRadioFilters.length > 0 && (
            <Box mt={2}>
              {bottomRadioFilters.map((group, idx) => (
                <Box key={group.key} mb={2}>
                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{
                      fontWeight: 'bold',
                      mt:
                        idx === 0 && topToggleFilters.length ? 2 : 0,
                    }}
                  >
                    {group.title}
                  </Typography>

                  <RadioGroup
                    row
                    value={group.selectedKey}
                    onChange={(e) => group.onChange(e.target.value)}
                  >
                    {group.options.map((opt) => (
                      <FormControlLabel
                        key={opt.key}
                        value={opt.key}
                        control={<Radio />}
                        label={opt.label}
                      />
                    ))}
                  </RadioGroup>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default FiltersWithScope;
